import { createHttpClient } from '@adapters/HttpClient';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FiltersApiAdapter } from '../adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '../adapters/TeamsApiAdapter';
import { getViewPreInvoicesFilterOptions } from '../config/filterOptions';
import { ViewPreInvoiceStorageTableRow } from '../models/ViewPreInvoices';
import { usePreInvoice } from './usePreInvoice';
import { useViewPreInvoices } from './useViewPreInvoices';
import { useViewPreInvoiceStorages } from './useViewPreInvoiceStorages';
import { useInvoicesPermissions } from '@modules/Invoices/InvoicesOrders/List/hooks/useInvoicesPermissions';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useCountries } from './useCountries';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  order: 'order_id',
  financial_status: 'financial_status',
  shipping_date: 'shipping_date',
  courier: 'courier_id',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

/**
 * useViewPreInvoicesMain - Hook principal para gestionar el detalle de una prefactura
 *
 * Orquesta todos los aspectos de la vista de shipments asociados a una prefactura:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas (id, order, shipping_date, courier)
 * - Selección múltiple para acciones masivas
 * - Sincronización bidireccional con query params de la URL
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */
export const useViewPreInvoicesMain = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const currency = useCurrency();

  const { preInvoiceId } = useParams<{ preInvoiceId: string }>();
  const preInvoiceNumericId = Number(preInvoiceId ?? '0');

  // 🎯 Cargar países para el filtro
  const { countries } = useCountries();

  // 🎯 Función para cargar opciones dinámicamente (simplificado para ViewPreInvoices)
  const loadColumnValues = useCallback(async (columnValue: string) => {
    try {
      const httpClient = createHttpClient(apiClientWH);
      const filtersAdapter = new FiltersApiAdapter(httpClient);

      switch (columnValue) {
        case 'payment_method_id': {
          const paymentMethods = await filtersAdapter.getPaymentMethods();
          return paymentMethods.map((method) => ({
            label: method.name,
            value: String(method.id),
          }));
        }
        case 'shop_id': {
          const shops = await filtersAdapter.getShops();
          return shops.map((shop) => ({
            label: shop.name,
            value: String(shop.id),
          }));
        }
        case 'courier_id': {
          const couriers = await filtersAdapter.getCouriers();
          return couriers.map((courier) => ({
            label: courier.name,
            value: String(courier.id),
          }));
        }
        case 'team_id': {
          const teamsAdapter = new TeamsApiAdapter(httpClient);
          const teams = await teamsAdapter.getTeamsChild();
          return teams.map((team) => ({
            label: team.name,
            value: team.id.toString(),
          }));
        }
        default:
          return [];
      }
    } catch (error) {
      console.error('🔍 loadColumnValues - Error:', error);
      return [];
    }
  }, []);

  // 🎯 Obtener filterOptions
  const filterOptions = useMemo(() => {
    return getViewPreInvoicesFilterOptions(t, loadColumnValues, true, countries || []);
  }, [t, loadColumnValues, countries]);

  // 🎯 Query Params (sincronización con URL)
  // ⚠️ NO incluimos selectedColumns en URL - se persisten en localStorage
  const { params: queryParams, updateParams: setQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.search as string);
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    parseFiltersFromParams(queryParams.filters),
  );
  // 🎯 Las columnas seleccionadas NO se sincronizan con URL, solo con localStorage
  // ColumnSelector maneja la persistencia automáticamente
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedViewPreInvoices, setSelectedViewPreInvoices] = useState<Set<number>>(new Set());

  // 🎯 Refs para detectar cambios en filtros
  const previousFiltersRef = useRef<string>('');
  const previousSearchRef = useRef<string>('');

  // 🎯 Sorting state
  const [sorting, setSorting] = useState<{
    field: string;
    direction: 'asc' | 'desc';
  } | null>(() => {
    const sortBy = queryParams.sortBy as string;
    const sortDirection = queryParams.sortDirection as 'asc' | 'desc';
    return sortBy ? { field: sortBy, direction: sortDirection || 'desc' } : null;
  });

  // 🎯 Construir searchFilter para la API
  const searchFilter = useMemo(() => {
    const filters: any[] = [];

    // Agregar condiciones de filtros avanzados
    if (filterConditions && filterConditions.length > 0) {
      filterConditions.forEach((condition) => {
        const apiField = fieldToApiMapping[condition.column] || condition.column;
        const operator = condition.action;
        if (!operator) {
          return;
        }

        // Operadores que no requieren valor
        if (operator === 'is_null' || operator === 'is_not_null') {
          filters.push({
            column: apiField,
            operator,
          });
          return;
        }

        const value = condition.value;

        // Normalizar valores para operadores que requieren arrays
        if (operator === 'in' || operator === 'not_in') {
          if (value === undefined || value === null || value === '') {
            return;
          }

          if (Array.isArray(value)) {
            filters.push({
              column: apiField,
              operator,
              value,
            });
          } else if (typeof value === 'string') {
            const normalized = value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);

            if (normalized.length === 0) {
              return;
            }

            filters.push({
              column: apiField,
              operator,
              value: normalized,
            });
          } else {
            filters.push({
              column: apiField,
              operator,
              value: [value],
            });
          }

          return;
        }

        // Evitar enviar valores vacíos
        if (value === undefined || value === null || value === '') {
          return;
        }

        filters.push({
          column: apiField,
          operator,
          value,
        });
      });
    }

    return filters.length > 0
      ? {
          filters,
          logic: 'AND',
        }
      : undefined;
  }, [filterConditions]);

  const normalizedSearch = useMemo(() => {
    const value = searchTerm?.trim();
    return value ? value : undefined;
  }, [searchTerm]);

  // 🎯 Obtener prefactura primero para determinar el tipo
  const { preInvoice } = usePreInvoice();
  const isStorageType = preInvoice?.type === 'STORAGE';

  // 🎯 Hook de datos con infinite scroll - según el tipo
  const {
    shipments: shipmentsData,
    isLoading: isLoadingShipments,
    isFetching: isFetchingShipments,
    error: errorShipments,
    total: totalShipments,
    refetch: refetchShipments,
    fetchNextPage: fetchNextPageShipments,
    hasNextPage: hasNextPageShipments,
    isFetchingNextPage: isFetchingNextPageShipments,
  } = useViewPreInvoices({
    preInvoiceId: preInvoiceNumericId,
    searchFilter,
    search: normalizedSearch,
    sortBy: sorting?.field || 'id',
    sortDirection: sorting?.direction || 'desc',
    enabled: !isStorageType, // Solo habilitar si NO es storage
  });

  const {
    storages: storagesData,
    isLoading: isLoadingStorages,
    isFetching: isFetchingStorages,
    error: errorStorages,
    total: totalStorages,
    refetch: refetchStorages,
    fetchNextPage: fetchNextPageStorages,
    hasNextPage: hasNextPageStorages,
    isFetchingNextPage: isFetchingNextPageStorages,
  } = useViewPreInvoiceStorages({
    preInvoiceId: preInvoiceNumericId,
    searchFilter,
    search: normalizedSearch,
    sortBy: sorting?.field || 'product_id',
    sortDirection: sorting?.direction || 'asc',
    enabled: isStorageType, // Solo habilitar si es storage
  });

  // 🎯 Unificar estados según el tipo
  const shipments = isStorageType ? [] : shipmentsData;
  const storages = isStorageType ? storagesData : [];
  const isLoading = isStorageType ? isLoadingStorages : isLoadingShipments;
  const isFetching = isStorageType ? isFetchingStorages : isFetchingShipments;
  const error = isStorageType ? errorStorages : errorShipments;
  const apiTotal = isStorageType ? totalStorages : totalShipments;
  const refetch = isStorageType ? refetchStorages : refetchShipments;
  const fetchNextPage = isStorageType ? fetchNextPageStorages : fetchNextPageShipments;
  const hasNextPage = isStorageType ? hasNextPageStorages : hasNextPageShipments;
  const isFetchingNextPage = isStorageType ? isFetchingNextPageStorages : isFetchingNextPageShipments;
  const { canEditLines, canRestoreLines, canBlockLines } = useInvoicesPermissions();
  const showActions = preInvoice?.status !== 'CLOSED' && (canEditLines || canRestoreLines || canBlockLines);

  // 🎯 Transformar storages en filas de tabla
  const storageTableRows: ViewPreInvoiceStorageTableRow[] = useMemo(() => {
    const formatVolume = (value: number) => {
      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value)} m³`;
    };

    const formatCurrency = (amount: number | null | undefined): string => {
      if (amount === null || amount === undefined) return '-';
      return formatCurrencyAmount(amount, currency);
    };

    const formatDate = (dateString: string) => {
      try {
        return new Date(dateString).toLocaleDateString('es-ES');
      } catch {
        return dateString;
      }
    };

    return storages.map((storage, rowIndex) => {
      const id =
        storage.id != null && Number.isFinite(storage.id)
          ? `storage-${storage.id}`
          : [
              'storage',
              rowIndex,
              storage.product_id,
              storage.team_id,
              storage.period,
              storage.min_storage_date,
              storage.max_storage_date,
              storage.total_volume_m3,
              storage.quantity,
            ].join('-');
      const dateRange =
        storage.min_storage_date === storage.max_storage_date
          ? formatDate(storage.min_storage_date)
          : `${formatDate(storage.min_storage_date)} - ${formatDate(storage.max_storage_date)}`;

      return {
        id,
        storageId: storage.id,
        productId: storage.product_id,
        product: storage.product?.name ?? '--',
        productCode: storage.product?.code ?? '--',
        team: storage.team?.name ?? '--',
        period: storage.period ?? '--',
        quantity: storage.quantity ?? 0,
        unitVolume: formatVolume(storage.unit_volume_m3),
        totalVolume: formatVolume(storage.total_volume_m3),
        dimensions: `${storage.width_m}m × ${storage.height_m}m × ${storage.length_m}m`,
        baseAmount: formatCurrency(storage.base_amount),
        totalAmount: formatCurrency(storage.total_amount),
        linesCount: storage.lines_count ?? 0,
        dateRange,
      };
    });
  }, [storages, currency]);

  const totalCount =
    typeof apiTotal === 'number' ? apiTotal : isStorageType ? storageTableRows.length : shipments.length;

  // 🎯 Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    const currentFilters = serializeConditions(filterConditions);
    const currentSearch = searchTerm?.trim() || '';

    // Actualizar refs
    previousFiltersRef.current = currentFilters;
    previousSearchRef.current = currentSearch;

    // Hay filtros activos si hay condiciones o búsqueda
    return (filterConditions && filterConditions.length > 0) || (searchTerm && searchTerm.trim() !== '');
  }, [filterConditions, searchTerm]);

  // 🎯 Sincronizar estados locales con query params al montar
  useEffect(() => {
    if (queryParams.search) {
      setSearchTerm(queryParams.search as string);
    }
    if (queryParams.filters) {
      setFilterConditions(parseFiltersFromParams(queryParams.filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // 🎯 Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      setQueryParams({ filters: conditions });
    },
    [setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setQueryParams({ search: value });
    },
    [setQueryParams],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    // 🎯 Solo actualizar estado local - NO sincronizar con URL
    // ColumnSelector se encarga de persistir en localStorage
    setSelectedColumns(columns);
  }, []);

  // 🎯 Handlers de sorting
  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);
      if (newSorting) {
        setQueryParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      }
    },
    [setQueryParams],
  );

  // 🎯 Handlers de selección
  const handleSelectionChanged = useCallback(
    (selectedIds: Set<number> | Set<string>) => {
      if (isStorageType) {
        const gridIds = selectedIds as Set<string>;
        const numeric = new Set<number>();
        for (const gridRowId of gridIds) {
          const row = storageTableRows.find((r) => r.id === gridRowId);
          const pid = row?.productId;
          if (typeof pid === 'number' && Number.isFinite(pid)) {
            numeric.add(pid);
          }
        }
        setSelectedViewPreInvoices(numeric);
      } else {
        // Para shipments, los IDs son números
        setSelectedViewPreInvoices(selectedIds as Set<number>);
      }
    },
    [isStorageType, storageTableRows],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedViewPreInvoices(new Set());
  }, []);

  // 🎯 Props para componentes de vista
  const viewPreInvoicesViewProps = useMemo(
    () => ({
      isLoading: isLoading && !isFetching,
      isFetching,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [isLoading, isFetching, sorting, handleSortingChange],
  );

  return {
    // Estados
    selectedViewPreInvoices,
    selectedColumns,

    // Datos
    shipments,
    storages,
    storageTableRows,
    isStorageType,
    isLoading,
    isFetching,
    error,
    total: totalCount,

    // Props para componentes
    viewPreInvoicesViewProps,
    showActions,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
    filterOptions,
    permissions: {
      canEditLines,
      canRestoreLines,
      canBlockLines,
    },
  };
};
